/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import type { MetadataElement } from '../services/api/files'

/**
 * Metadata types enum
 */
export const MetadataType = {
  STRING: 'STRING',
  NUMBER: 'NUMBER',
  DATETIME: 'DATETIME',
  SELECT_ONE: 'SELECT_ONE',
  SELECT_MULTI: 'SELECT_MULTI',
  SELECT_ONE_USER: 'SELECT_ONE_USER',
  SELECT_MULTI_USER: 'SELECT_MULTI_USER',
  SELECT_ONE_GROUP: 'SELECT_ONE_GROUP',
  SELECT_MULTI_GROUP: 'SELECT_MULTI_GROUP',
  ARTIFACT: 'ARTIFACT'
} as const

export type MetadataTypeValue = (typeof MetadataType)[keyof typeof MetadataType]

/**
 * Check if a property value is missing/empty
 * Used for required property validation
 *
 * @param property - Metadata property element
 * @param metaType - Metadata type constants (optional, uses property.template.type if not provided)
 * @returns true if value is missing/invalid, false if value is present
 */
export function checkIsPropertyValueMissing(property: MetadataElement, metaType?: typeof MetadataType): boolean {
  const type = property.template?.type

  switch (type) {
    case MetadataType.NUMBER:
      // Number: must be a number (including 0)
      return !(property.value || property.value === 0)

    case MetadataType.SELECT_MULTI:
    case MetadataType.SELECT_MULTI_USER:
    case MetadataType.SELECT_MULTI_GROUP:
      // Multi-select: must have at least one selection
      return !property.value || property.value.length === 0

    default:
      // String, datetime, select_one, etc: must have non-empty trimmed value
      if (!property.value) {
        return true
      }

      const stringValue = property.value.toString().trim()
      return stringValue.length === 0
  }
}

/**
 * Check if a property is required and has missing value
 * @param property - Metadata property element
 * @param metaType - Metadata type constants
 */
export function isPropertyRequired(property: MetadataElement): boolean {
  return (
    property.requiredOnFSITemplate === true &&
    property.checkRequired === true &&
    ((property.mixedValue === true && property.mixedValueRequiredEmpty === true) || !property.mixedValue)
  )
}

/**
 * Validate all properties and return error state
 * @param metadata - Array of metadata elements
 * @returns Object with error flags
 */
export function validateProperties(metadata: MetadataElement[]): {
  requiredPropsAreMissing: boolean
  missingProperties: MetadataElement[]
} {
  const missingProperties: MetadataElement[] = []

  if (Array.isArray(metadata)) {
    metadata.forEach((property) => {
      if (isPropertyRequired(property) && checkIsPropertyValueMissing(property)) {
        missingProperties.push(property)
      }
    })
  }

  return {
    requiredPropsAreMissing: missingProperties.length > 0,
    missingProperties
  }
}
